
import { defineComponent, onMounted, reactive, ref, watch } from 'vue';
import { Search as SearchIcon, Info as InfoIcon, ArrowLeft as ResetIcon, ArrowUp, ArrowDown, Cog as EditIcon, Save, CaretDown } from '@vicons/fa';
import { http, EthersService as eth, houndPackName, resetUserPreferences } from '../services';
import { HoundModel, FindAllResponse, formatNumber } from '../../../common';
import { defaultPageSize } from '@/data/global';
import { FormRules } from 'naive-ui';
import { toast } from '@/utils/notification.util';
import HoundListItem from './HoundListItem.vue';
import breakpoints from '../utils/breakpoints';

enum SortTypes {
  TOTAL_RUNS='totalRuns',
  FIRST_PLACE='firstPlace',
  SECOND_PLACE='secondPlace',
  THIRD_PLACE='thirdPlace'
}

enum OrderTypes {
  ASC='ASC',
  DESC='DESC'
}

export default defineComponent({
  components: {
    SearchIcon,
    InfoIcon,
    ResetIcon,
    EditIcon,
    ArrowUp,
    ArrowDown,
    Save,
    HoundListItem,
    CaretDown
  },
  name: 'Hounds',
  setup () {
    const page = ref(1);
    const pageSize = ref(defaultPageSize.value);

    const hounds = ref<Array<HoundModel>>([]);
    const totalHounds = ref(0);

    let commitedFilters = {
      title: '',
      sort: null,
      order: OrderTypes.ASC,
      gender: undefined,
      minStamina: 0
    };
    let commitedFiltersEmpty = ref(true);
    const filtersModel = reactive({ ...commitedFilters });
    const showNamePopover = ref(false);
    const nameFormRef = ref(null);
    const nameModel = reactive({ name: houndPackName.value });

    const search = () => {
      page.value = 1;
      commitedFilters = { ...filtersModel };
      commitedFiltersEmpty.value = !commitedFilters.title && !commitedFilters.gender;
      resetHounds();
    };

    const resetHounds = (): void => {
      hounds.value = [];
      http.getOwnHounds({
        name: commitedFilters.title,
        limit: pageSize.value,
        offset: (page.value - 1) * pageSize.value,
        orderBy: commitedFilters.sort ? `${commitedFilters.sort}-${commitedFilters.order}` : 'birthDate-DESC',
        sex: commitedFilters.gender,
        minStamina: commitedFilters.minStamina
      }).then((res: FindAllResponse<HoundModel>) => {
        hounds.value = [];
        res.data.forEach(e => {
          hounds.value.push(new HoundModel(e));
        });
        totalHounds.value = res.total;
      });
    };

    const resetFilters = (): void => {
      filtersModel.title = '';
      filtersModel.sort = null;
      filtersModel.gender = undefined;
      filtersModel.minStamina = 0;
      commitedFiltersEmpty.value = true;
      search();
    };

    if (eth.initialized) resetHounds();
    watch(() => eth.reactive.value.userAddress, (_) => {
      resetHounds();
    });
    const changePagination = (ev: number, size: boolean = false): void => {
      if (size) {
        pageSize.value = ev;
        page.value = 1;
      }
      resetHounds();
    };

    const nameRules: FormRules = {
      name: [{
        trigger: ['blur', 'input'],
        required: true,
        message: 'Pack name is required'
      }]
    }

    const updateName = () => {
      nameFormRef.value?.validate(async (errors) => {
        if (errors) {
          toast.error('Invalid values');
          return;
        }
        eth.signer.signMessage('Update hound pack name').then(async (value) => {
          const address = await eth.signer.getAddress();
          const preferences = await http.updatePackName({
            owner: address,
            pack_name: nameModel.name,
            signedMsg: value
          });
          nameModel.name = preferences.pack_name;
          resetUserPreferences();
          showNamePopover.value = false;
          toast.success('Pack name updated');
        });
      });
    };
    
    const winCashCoop = ref<{
      firstPlace: number,
      secondPlace: number,
      thirdPlace: number
    }>();
    onMounted(() => {
      http.getWinCashCoop().then((data: any) => {
        winCashCoop.value = data;
      })
    });

    return {
      packName: houndPackName,
      commitedFiltersEmpty,
      filtersModel,
      nameFormRef,
      nameModel,
      nameRules,
      search,
      changePagination,
      resetFilters,
      winCashCoop,
      hounds,
      totalHounds,
      ethReactive: eth.reactive,
      page,
      pageSize,
      formatNumber,
      updateName,
      showNamePopover,
      resetHounds,
      breakpoints,
      sortTypes: [
        {
          value: SortTypes.TOTAL_RUNS,
          label: 'Total Runs'
        },
        {
          value: SortTypes.FIRST_PLACE,
          label: 'Won 1st'
        },
        {
          value: SortTypes.SECOND_PLACE,
          label: 'Won 2nd'
        },
        {
          value: SortTypes.THIRD_PLACE,
          label: 'Won 3rd'
        }
      ],
      genderTypes: [
        {
          value: null,
          label: 'Any'
        },
        {
          value: 1,
          label: 'Male'
        },
        {
          value: 2,
          label: 'Female'
        }
      ]
    };
  }
});
